import type { CapabilitiesRegistry as ICapabilitiesRegistry } from '@gzl10/nexus-sdk'

type Resolver = () => unknown | Promise<unknown>

interface MinimalLogger {
  warn: (obj: Record<string, unknown>, msg: string) => void
}

/**
 * Extensible capabilities registry.
 *
 * Plugins register resolvers via `ctx.capabilities.register(key, resolver)`.
 * `resolve()` runs all resolvers in parallel with individual error catching.
 */
export class CapabilitiesRegistry implements ICapabilitiesRegistry {
  private resolvers = new Map<string, Resolver>()
  private logger?: MinimalLogger

  constructor(logger?: MinimalLogger) {
    this.logger = logger
  }

  register(key: string, resolver: Resolver): void {
    if (this.resolvers.has(key)) {
      this.logger?.warn({ key }, 'Capability key already registered, overwriting')
    }
    this.resolvers.set(key, resolver)
  }

  async resolve(): Promise<Record<string, unknown>> {
    const entries = Array.from(this.resolvers.entries())
    const results = await Promise.allSettled(
      entries.map(async ([key, resolver]) => {
        const value = await resolver()
        return [key, value] as const
      })
    )

    const capabilities: Record<string, unknown> = {}
    for (const result of results) {
      if (result.status === 'fulfilled') {
        const [key, value] = result.value
        capabilities[key] = value
      } else {
        this.logger?.warn(
          { err: result.reason?.message ?? String(result.reason) },
          'Capability resolver failed'
        )
      }
    }
    return capabilities
  }
}
